<template>
  <div class="banner-container" v-if="banners && banners.length > 0">
    <div 
      class="banner-wrapper" 
      :style="{ transform: `translateX(-${currentIndex * 100}%)` }"
    >
      <div 
        v-for="(banner, index) in banners" 
        :key="index" 
        class="banner-item"
        @click="handleBannerClick(banner)"
      >
        <!-- 图片类型 -->
        <img 
          v-if="banner.type === 'image'" 
          :src="banner.src" 
          :alt="banner.alt || 'Banner image'" 
          class="banner-media banner-image"
          loading="lazy"
        />
        <!-- 视频类型 -->
        <video 
          v-else-if="banner.type === 'video'" 
          :src="banner.src" 
          :poster="banner.poster" 
          class="banner-media banner-video"
          autoplay 
          muted 
          loop 
          playsinline
        ></video>
        
        <!-- 可选的文字内容 -->
        <div class="banner-content" v-if="banner.title || banner.description">
          <h2 class="banner-title">{{ banner.title }}</h2>
          <p class="banner-description">{{ banner.description }}</p>
        </div>
      </div>
    </div>
    
    <!-- 指示器 -->
    <div class="banner-indicators">
      <span 
        v-for="(banner, index) in banners" 
        :key="index" 
        class="indicator" 
        :class="{ active: index === currentIndex }"
        @click="goToSlide(index)"
      ></span>
    </div>
    

  </div>
</template>

<script>
export default {
  name: 'Banner',
  props: {
    banners: {
      type: Array,
      required: true,
      default: () => []
    },
    interval: {
      type: Number,
      default: 5000 // 默认轮播间隔时间
    },
    autoplay: {
      type: Boolean,
      default: true // 是否自动轮播
    }
  },
  data() {
    return {
      currentIndex: 0,
      timer: null
    }
  },
  computed: {
    totalSlides() {
      return this.banners.length
    }
  },
  mounted() {
    // 初始化自动轮播
    if (this.autoplay) {
      this.startAutoplay()
    }
  },
  beforeUnmount() {
    // 清理定时器
    this.stopAutoplay()
  },
  methods: {
    // 处理Banner点击事件
    handleBannerClick(banner) {
      if (banner.linkUrl) {
        window.open(banner.linkUrl, '_blank');
      }
    },
    
    // 开始自动轮播
    startAutoplay() {
      this.timer = setInterval(() => {
        this.nextSlide()
      }, this.interval)
    },
    
    // 停止自动轮播
    stopAutoplay() {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },
    
    // 下一张
    nextSlide() {
      this.currentIndex = (this.currentIndex + 1) % this.totalSlides
    },
    
    // 上一张
    prevSlide() {
      this.currentIndex = (this.currentIndex - 1 + this.totalSlides) % this.totalSlides
    },
    
    // 跳转到指定索引的轮播图
    goToSlide(index) {
      this.currentIndex = index
    },
    
    // 鼠标移入时暂停轮播
    handleMouseEnter() {
      this.stopAutoplay()
    },
    
    // 鼠标移出时恢复轮播
    handleMouseLeave() {
      if (this.autoplay) {
        this.startAutoplay()
      }
    }
  },
  watch: {
    // 监听banners数组变化，重置索引
    banners: {
      handler() {
        if (this.totalSlides > 0 && this.currentIndex >= this.totalSlides) {
          this.currentIndex = 0
        }
      },
      deep: true
    },
    
    // 监听autoplay状态变化
    autoplay(newVal) {
      if (newVal) {
        this.startAutoplay()
      } else {
        this.stopAutoplay()
      }
    }
  }
}
</script>

<style scoped>
.banner-container {
  position: relative;
  width: 100%;
  height: 100%; /* 默认高度，可以根据需要调整 */
  overflow: hidden;
  cursor: pointer;
}

.banner-wrapper {
  display: flex;
  height: 100%;
  transition: transform 0.5s ease-in-out;
}

.banner-item {
  flex: 0 0 100%;
  position: relative;
  height: 100%;
}

.banner-media {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.banner-content {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 2rem;
  background: linear-gradient(to top, rgba(0,0,0,0.7), transparent);
  color: white;
}

.banner-title {
  margin: 0 0 0.5rem 0;
  font-size: 2rem;
}

.banner-description {
  margin: 0;
  font-size: 1rem;
  opacity: 0.9;
}

.banner-indicators {
  position: absolute;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 0.5rem;
  z-index: 10;
}

.indicator {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.indicator.active {
  background-color: white;
  transform: scale(1.2);
}

.control-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background-color: rgba(0, 0, 0, 0.3);
  color: white;
  border: none;
  width: 40px;
  height: 40px;
  font-size: 1.5rem;
  cursor: pointer;
  border-radius: 50%;
  transition: background-color 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.control-btn:hover {
  background-color: rgba(0, 0, 0, 0.5);
}

.prev-btn {
  left: 1rem;
}

.next-btn {
  right: 1rem;
}

/* 响应式设计 */
@media (max-width: 768px) {
  
  .banner-title {
    font-size: 1.5rem;
  }
  
  .banner-description {
    font-size: 0.9rem;
  }
  
  .control-btn {
    width: 30px;
    height: 30px;
    font-size: 1rem;
  }
}
</style>